import React, { useEffect, useState, useCallback, useRef } from 'react'

interface ComponentInfo {
  name: string
  path: string
  rect: DOMRect
}

interface InspectorModeProps {
  enabled: boolean
  mode: 'blue' | 'red'
  onSelect: (componentInfo: ComponentInfo) => void
  onClose: () => void
  onAIAssist?: (componentInfo: ComponentInfo) => void
}

export const InspectorMode: React.FC<InspectorModeProps> = ({
  enabled,
  mode,
  onSelect,
  onClose,
  onAIAssist,
}) => {
  const [elementsAtPoint, setElementsAtPoint] = useState<Element[]>([])
  const [depthIndex, setDepthIndex] = useState(0)
  const [tooltip, setTooltip] = useState<{ x: number; y: number } | null>(null)
  const lastPoint = useRef<{ x: number; y: number }>({ x: 0, y: 0 })

  const hoveredElement = elementsAtPoint[depthIndex] || null

  const getComponentName = useCallback((element: Element): string => {
    const dataComponent = element.getAttribute('data-component')
    if (dataComponent) return dataComponent

    const className = element.getAttribute('class')
    if (className) {
      const classes = className.split(' ')
      const meaningfulClass = classes.find(
        (c) => !c.startsWith('_') && c.length > 2
      )
      if (meaningfulClass) return meaningfulClass
    }

    const tagName = element.tagName.toLowerCase()
    const id = element.getAttribute('id')
    if (id) return `${tagName}#${id}`

    return tagName
  }, [])

  const getElementPath = useCallback((element: Element): string => {
    const path: string[] = []
    let current: Element | null = element

    while (current && current !== document.body) {
      const name = getComponentName(current)
      path.unshift(name)
      current = current.parentElement
    }

    return path.join(' > ')
  }, [getComponentName])

  // Get all elements at a specific point, from top to bottom
  const getElementsAtPoint = useCallback((x: number, y: number): Element[] => {
    const elements: Element[] = []
    let element = document.elementFromPoint(x, y)

    while (element && element !== document.documentElement) {
      // Skip our inspector overlay elements
      if (!element.closest('[data-inspector]')) {
        elements.push(element)
      }
      element = element.parentElement
    }

    return elements
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!enabled) return

      lastPoint.current = { x: e.clientX, y: e.clientY }
      const elements = getElementsAtPoint(e.clientX, e.clientY)

      setElementsAtPoint(elements)
      // Reset depth when moving to new position
      if (elements.length > 0 && elements[0] !== elementsAtPoint[0]) {
        setDepthIndex(0)
      }

      setTooltip({ x: e.clientX + 15, y: e.clientY + 15 })
    },
    [enabled, getElementsAtPoint, elementsAtPoint]
  )

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      if (!enabled || elementsAtPoint.length === 0) return

      e.preventDefault()
      e.stopPropagation()

      // Scroll down = go deeper (higher index), scroll up = go shallower
      if (e.deltaY > 0) {
        setDepthIndex((prev) => Math.min(prev + 1, elementsAtPoint.length - 1))
      } else {
        setDepthIndex((prev) => Math.max(prev - 1, 0))
      }
    },
    [enabled, elementsAtPoint.length]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return

      if (e.key === 'Escape') {
        onClose()
        return
      }

      // Arrow keys to cycle depth
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault()
        setDepthIndex((prev) => Math.min(prev + 1, elementsAtPoint.length - 1))
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault()
        setDepthIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' && hoveredElement) {
        // Enter to select current element
        e.preventDefault()
        const rect = hoveredElement.getBoundingClientRect()
        onSelect({
          name: getComponentName(hoveredElement),
          path: getElementPath(hoveredElement),
          rect,
        })
      }
    },
    [enabled, elementsAtPoint.length, hoveredElement, getComponentName, getElementPath, onSelect, onClose]
  )

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!enabled || !hoveredElement) return

      e.preventDefault()
      e.stopPropagation()

      const rect = hoveredElement.getBoundingClientRect()
      const name = getComponentName(hoveredElement)
      const path = getElementPath(hoveredElement)

      const info = { name, path, rect }

      if (mode === 'red' && onAIAssist) {
        onAIAssist(info)
      } else {
        onSelect(info)
      }
    },
    [enabled, hoveredElement, getComponentName, getElementPath, onSelect, onAIAssist, mode]
  )

  useEffect(() => {
    if (!enabled) return

    document.addEventListener('mousemove', handleMouseMove, true)
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true })
    document.addEventListener('click', handleClick, true)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, true)
      document.removeEventListener('wheel', handleWheel, true)
      document.removeEventListener('click', handleClick, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [enabled, handleMouseMove, handleWheel, handleClick, handleKeyDown])

  // Reset state when disabled
  useEffect(() => {
    if (!enabled) {
      setElementsAtPoint([])
      setDepthIndex(0)
      setTooltip(null)
    }
  }, [enabled])

  if (!enabled) return null

  const componentName = hoveredElement ? getComponentName(hoveredElement) : ''
  const rect = hoveredElement?.getBoundingClientRect()

  // Mode-based colors
  const colors = mode === 'red'
    ? { primary: '#ef4444', light: 'rgba(239, 68, 68, 0.1)', text: '#fca5a5' }
    : { primary: '#3b82f6', light: 'rgba(59, 130, 246, 0.1)', text: '#60a5fa' }

  return (
    <>
      {/* Overlay */}
      <div
        data-inspector="overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: mode === 'red' ? 'rgba(239, 68, 68, 0.05)' : 'rgba(0, 0, 0, 0.1)',
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      />

      {/* Highlight border */}
      {rect && (
        <div
          data-inspector="highlight"
          style={{
            position: 'fixed',
            border: `2px solid ${colors.primary}`,
            pointerEvents: 'none',
            zIndex: 10000,
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            boxShadow: `0 0 0 2000px ${colors.light}`,
            transition: 'all 0.1s ease',
          }}
        />
      )}

      {/* Tooltip with depth indicator */}
      {tooltip && componentName && (
        <div
          data-inspector="tooltip"
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            backgroundColor: '#1f2937',
            color: '#f3f4f6',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            pointerEvents: 'none',
            zIndex: 10001,
            maxWidth: '350px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ color: colors.text, fontWeight: 600 }}>{componentName}</span>
            <span style={{
              background: colors.primary,
              color: 'white',
              fontSize: '10px',
              padding: '2px 6px',
              borderRadius: '4px',
            }}>
              {mode === 'red' ? 'AI' : ''} {depthIndex + 1}/{elementsAtPoint.length}
            </span>
          </div>
          <div style={{ color: '#9ca3af', fontSize: '10px' }}>
            {mode === 'red' ? 'Click to bug fix this component' : 'Scroll or ↑↓ to change depth • Click to select'}
          </div>
        </div>
      )}
    </>
  )
}
